/**
 * EngineAtlasInspectRouter — dispatches by `:subRegionId` URL param to the
 * appropriate inspect view per Atlas Day 2.5 P1 URL routing infrastructure.
 *
 * Per Atlas Day 2.5 visual-layout commitment B.3 — clicked sub-region opens
 * full-page inspect view replacing map. URL routing implementation per
 * build prompt §1 (recommended path; React Router already a dep).
 *
 * INSPECT_VIEWS dispatch map grows as inspect views land:
 *   - Atlas Day 2 P6 shipped PerDistributorInspect (canonical pattern)
 *   - Atlas Day 2.5 P3-P7 added 7 substrate-row inspect views (8 total)
 *   - Atlas Day 3a P3 adds 6 transformation-row inspect views (14 total):
 *     Engine calculations region + 5 Decision rules cluster sub-regions
 *
 * Unknown subRegionId falls through to InspectNotFound — breadcrumb +
 * ESC + browser back all still work via React Router.
 */

import type { JSX, ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import PerDistributorInspect from './PerDistributorInspect';
import PerSupplierInspect from './PerSupplierInspect';
import PerBrandInspect from './PerBrandInspect';
import PerManufacturerInspect from './PerManufacturerInspect';
import PerCategoryInspect from './PerCategoryInspect';
import BusinessDefaultsInspect from './BusinessDefaultsInspect';
import PathMechanicsInspect from './PathMechanicsInspect';
import WeightComponentsInspect from './WeightComponentsInspect';
import EngineCalculationsInspect from './EngineCalculationsInspect';
import MapEnforcementInspect from './MapEnforcementInspect';
import CreditGateInspect from './CreditGateInspect';
import PathEligibilityInspect from './PathEligibilityInspect';
import RestockUrgencyTiersInspect from './RestockUrgencyTiersInspect';
import BuyScoreCompositionInspect from './BuyScoreCompositionInspect';
import InspectNotFound from './InspectNotFound';

const INSPECT_VIEWS: Record<string, ComponentType> = {
  // Substrate-row inspect views (Atlas Day 2 + Day 2.5)
  'per-distributor': PerDistributorInspect,
  'per-supplier': PerSupplierInspect,
  'per-brand': PerBrandInspect,
  'per-manufacturer': PerManufacturerInspect,
  'per-category': PerCategoryInspect,
  'business-defaults': BusinessDefaultsInspect,
  'path-mechanics': PathMechanicsInspect,
  'weight-components': WeightComponentsInspect,
  // Transformation-row inspect views (Atlas Day 3a)
  'engine-calculations': EngineCalculationsInspect,
  'map-enforcement': MapEnforcementInspect,
  'credit-gate': CreditGateInspect,
  'path-eligibility': PathEligibilityInspect,
  'restock-urgency-tiers': RestockUrgencyTiersInspect,
  'buy-score-composition': BuyScoreCompositionInspect,
};

export default function EngineAtlasInspectRouter(): JSX.Element {
  const { subRegionId } = useParams<{ subRegionId: string }>();
  const InspectView = subRegionId ? INSPECT_VIEWS[subRegionId] : undefined;

  if (!InspectView) {
    return <InspectNotFound subRegionId={subRegionId} />;
  }

  return <InspectView />;
}
